import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PageHeading, NtIcon } from '../components/NotionUI';
import {
  getTicketProjectFields,
  createTicketProjectField,
  updateTicketProjectField,
  deleteTicketProjectField,
  reorderTicketProjectFields,
  getTicketFieldsLibrary,
  addTicketProjectFieldFromLibrary,
  getTicketProjects,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { resolveFieldLabel } from '../utils/fieldLabels';

const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'email', 'phone', 'url', 'select', 'checkbox', 'attachment'];

// Icona (NtIcon) di ogni tipo di campo.
const FIELD_NT_ICON = {
  text: 'text',
  textarea: 'list',
  number: 'hash',
  date: 'calendar',
  email: 'mail',
  phone: 'phone',
  url: 'link',
  select: 'tag',
  checkbox: 'priority',
  attachment: 'paperclip',
};

// Tenere allineato a PublicTicketController::DEFAULT_MAX_ATTACHMENTS (backend) -
// usato quando un campo "attachment" non specifica ancora un max_files proprio.
const DEFAULT_MAX_ATTACHMENTS = 5;

const emptyField = {
  field_type: 'text',
  is_required: false,
  labels: {},
  options: [],
  maxAttachments: DEFAULT_MAX_ATTACHMENTS,
  share_with_other_projects: false,
};

// Campi aggiuntivi (oltre a Nome/Email/Oggetto/Descrizione, sempre presenti
// su ogni form e non gestibili da qui) proposti per un
// progetto — coprono i casi più comuni di un modulo di richiesta stile
// Jira Service Management, così l'admin non deve ricrearli uno per uno.
const RECOMMENDED_FIELDS = [
  {
    field_type: 'attachment',
    is_required: false,
    labels: { 'it-IT': 'Allegato', 'en-GB': 'Attachment' },
    options: { max_files: DEFAULT_MAX_ATTACHMENTS },
  },
  {
    field_type: 'select',
    is_required: false,
    labels: { 'it-IT': 'Tipo problema', 'en-GB': 'Problem type' },
    options: [
      { value: 'hw', labels: { 'it-IT': 'Hardware', 'en-GB': 'Hardware' } },
      { value: 'sw', labels: { 'it-IT': 'Software', 'en-GB': 'Software' } },
    ],
  },
];

function normalizeLabel(value) {
  return (value || '').trim().toLowerCase();
}

// Due campi sono "lo stesso campo" se hanno lo stesso tipo/opzioni e, per
// ogni lingua presente in ENTRAMBE le etichette, lo stesso testo — senza
// richiedere che i due abbiano esattamente lo stesso insieme di lingue
// (un progetto con meno lingue configurate non deve creare un "duplicato"
// solo perché gli mancano etichette che l'altro campo ha in più).
function labelsOverlapMatch(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  let hasOverlap = false;
  for (const key of keys) {
    const av = normalizeLabel(a?.[key]);
    const bv = normalizeLabel(b?.[key]);
    if (av && bv) {
      hasOverlap = true;
      if (av !== bv) return false;
    }
  }
  return hasOverlap;
}

// Stesso principio delle etichette: le opzioni di un "select" combaciano se
// hanno lo stesso insieme di valori, ognuno con etichette che si sovrappongono
// — un confronto testuale esatto (JSON.stringify) è fragile all'ordine delle
// chiavi, che può cambiare passando per il database.
function optionsMatch(a, b) {
  const listA = a || [];
  const listB = b || [];
  if (listA.length !== listB.length) return false;
  const usedIndexes = new Set();
  return listA.every((optA) => {
    const matchIndex = listB.findIndex(
      (optB, i) => !usedIndexes.has(i) && optA.value === optB.value && labelsOverlapMatch(optA.labels, optB.labels)
    );
    if (matchIndex === -1) return false;
    usedIndexes.add(matchIndex);
    return true;
  });
}

// Il confronto delle opzioni si applica solo ai "select" (le scelte sono
// parte dell'identità del campo). Per "attachment" le opzioni contengono
// max_files, che è un parametro per-progetto, non un tratto distintivo del
// campo: due campi "Allegato" restano "lo stesso campo" anche con un
// numero massimo diverso, altrimenti ogni progetto ne creerebbe una copia
// indipendente nella libreria invece di condividerlo.
function fieldsMatch(a, b) {
  if (a.field_type !== b.field_type || !labelsOverlapMatch(a.labels, b.labels)) return false;
  return a.field_type === 'select' ? optionsMatch(a.options, b.options) : true;
}

function TicketProjectFields({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = Number(id);

  const [project, setProject] = useState(null);
  const [fields, setFields] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(emptyField);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [addingFieldId, setAddingFieldId] = useState(null);
  const [addingRecommendedIndex, setAddingRecommendedIndex] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [fieldsData, libraryData, projectsData] = await Promise.all([
        getTicketProjectFields(projectId),
        getTicketFieldsLibrary(),
        getTicketProjects(),
      ]);
      setFields(fieldsData.fields || []);
      setLibrary(libraryData.fields || []);
      setProject((projectsData.projects || []).find((p) => p.id === projectId) || null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const languages = project?.languages && project.languages.length ? project.languages : ['it-IT'];
  const defaultLanguage = project?.default_language || 'it-IT';

  // Lo stesso campo (stesso tipo/nome) può esistere su più progetti diversi:
  // nella libreria va mostrato una volta sola, con l'elenco di tutti i
  // progetti in cui è già usato, non una riga per ciascuno — e non deve
  // comparire se il progetto corrente ha già un campo equivalente.
  const libraryClusters = [];
  library.forEach((f) => {
    const cluster = libraryClusters.find((c) => fieldsMatch(c.representative, f));
    if (cluster) {
      cluster.projectNames.add(f.project_name);
    } else {
      libraryClusters.push({ representative: f, projectNames: new Set([f.project_name]) });
    }
  });
  // I campi consigliati (vedi sotto) vengono mostrati a parte, sempre in
  // cima: se uno di essi è anche riusabile da un altro progetto, va escluso
  // da qui per non farlo comparire due volte con etichette diverse.
  const availableLibrary = libraryClusters
    .filter((c) => !fields.some((used) => fieldsMatch(used, c.representative)))
    .filter((c) => !RECOMMENDED_FIELDS.some((rf) => fieldsMatch(rf, c.representative)))
    .map((c) => ({ ...c.representative, usedInProjectNames: Array.from(c.projectNames) }));

  // Per ogni campo consigliato, se esiste già altrove lo segnaliamo insieme
  // al tag "consigliato" invece di sdoppiarlo in due righe separate.
  const recommendedWithUsage = RECOMMENDED_FIELDS.map((rf) => {
    const cluster = libraryClusters.find((c) => fieldsMatch(c.representative, rf));
    return { field: rf, usedInProjectNames: cluster ? Array.from(cluster.projectNames) : [] };
  });

  // Solo i campi consigliati non ancora presenti — il pulsante "Aggiungi
  // campi consigliati" resta disponibile anche a modifica avanzata, per
  // completare un progetto configurato solo in parte, senza mai duplicare
  // o toccare i campi già aggiunti.
  const missingRecommended = RECOMMENDED_FIELDS.filter((rf) => !fields.some((f) => fieldsMatch(f, rf)));

  const startCreate = () => {
    setForm({ ...emptyField, labels: {} });
    setExpandedId('new');
  };

  const startEdit = (field) => {
    setForm({
      field_type: field.field_type,
      is_required: field.is_required,
      labels: { ...field.labels },
      options: field.field_type === 'select' ? (field.options || []).map((o) => ({ ...o, labels: { ...o.labels } })) : [],
      maxAttachments: field.field_type === 'attachment' ? (field.options?.max_files || DEFAULT_MAX_ATTACHMENTS) : DEFAULT_MAX_ATTACHMENTS,
      share_with_other_projects: false,
    });
    setExpandedId(field.id);
  };

  const cancelEdit = () => setExpandedId(null);

  const setLabel = (code, value) => {
    setForm((f) => ({ ...f, labels: { ...f.labels, [code]: value } }));
  };

  const addOption = () => {
    setForm((f) => ({ ...f, options: [...f.options, { value: '', labels: {} }] }));
  };

  const updateOption = (index, patch) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));
  };

  const removeOption = (index) => {
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let options = null;
      if (form.field_type === 'select') {
        options = form.options;
      } else if (form.field_type === 'attachment') {
        options = { max_files: Math.max(1, Number(form.maxAttachments) || DEFAULT_MAX_ATTACHMENTS) };
      }
      const payload = {
        field_type: form.field_type,
        is_required: form.is_required,
        labels: form.labels,
        options,
      };
      const isDuplicate = fields.some((f) => f.id !== expandedId && fieldsMatch(f, payload));
      if (isDuplicate) {
        setError(t('tickets.fieldDuplicateError'));
        setSaving(false);
        return;
      }
      if (expandedId && expandedId !== 'new') {
        await updateTicketProjectField(expandedId, payload);
      } else {
        await createTicketProjectField(projectId, { ...payload, share_with_other_projects: form.share_with_other_projects });
      }
      setExpandedId(null);
      load();
    } catch (err) {
      setError(errorMessage(err, t('tickets.fieldSaveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field) => {
    if (deletingId) return;
    if (!window.confirm(t('tickets.fieldDeleteConfirm', { name: resolveFieldLabel(field, lang, defaultLanguage) }))) return;
    setDeletingId(field.id);
    setError('');
    try {
      await deleteTicketProjectField(field.id);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('tickets.fieldDeleteError')));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddFromLibrary = async (field) => {
    if (addingFieldId) return;
    setAddingFieldId(field.id);
    setError('');
    try {
      await addTicketProjectFieldFromLibrary(projectId, field.id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAddingFieldId(null);
    }
  };

  const handleAddRecommendedSingle = async (field, index) => {
    if (addingRecommendedIndex !== null) return;
    setAddingRecommendedIndex(index);
    setError('');
    try {
      await createTicketProjectField(projectId, field);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('tickets.fieldSaveError')));
    } finally {
      setAddingRecommendedIndex(null);
    }
  };

  const handleAddRecommended = async () => {
    setSeeding(true);
    setError('');
    try {
      for (const field of missingRecommended) {
        // eslint-disable-next-line no-await-in-loop
        await createTicketProjectField(projectId, field);
      }
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSeeding(false);
    }
  };

  const handleDragOverRow = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setFields((prev) => {
      const fromIndex = prev.findIndex((f) => f.id === dragId);
      const toIndex = prev.findIndex((f) => f.id === targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleDragEnd = async () => {
    const wasDragging = dragId !== null;
    setDragId(null);
    if (!wasDragging) return;
    try {
      await reorderTicketProjectFields(projectId, fields.map((f) => f.id));
    } catch (err) {
      setError(errorMessage(err));
      load();
    }
  };

  const renderEditForm = () => (
    <form className="fd-edit" onSubmit={handleSubmit}>
      <div className="fd-edit-block">
        <div className="fd-edit-label">{t('tickets.fieldType')}</div>
        <div className="fd-types" role="radiogroup" aria-label={t('tickets.fieldType')}>
          {FIELD_TYPES.map((ft) => (
            <button
              key={ft}
              type="button"
              role="radio"
              aria-checked={form.field_type === ft}
              className={'cm-type' + (form.field_type === ft ? ' cm-type-active' : '')}
              onClick={() => setForm({ ...form, field_type: ft })}
            >
              <NtIcon name={FIELD_NT_ICON[ft]} size={16} />
              <span>{t(`tickets.fieldType_${ft}`)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="fd-edit-block fd-labels">
        {languages.map((code) => (
          <div className="form-group" key={code}>
            <label>{t('tickets.fieldLabelFor', { language: code })}</label>
            <input value={form.labels[code] || ''} onChange={(e) => setLabel(code, e.target.value)} />
          </div>
        ))}
      </div>

      {form.field_type === 'select' && (
        <div className="fd-edit-block">
          <div className="fd-edit-label">{t('tickets.fieldOptions')}</div>
          {form.options.length > 0 && (
            <div className="fd-option fd-option-head" aria-hidden="true">
              <span className="fd-option-value">{t('tickets.fieldOptionValue')}</span>
              {languages.map((code) => <span key={code} className="fd-option-lang">{code}</span>)}
              <span className="fd-option-gap" />
            </div>
          )}
          {form.options.map((opt, index) => (
            <div key={index} className="fd-option">
              <input
                className="fd-option-value"
                placeholder={t('tickets.fieldOptionValue')}
                aria-label={t('tickets.fieldOptionValue')}
                value={opt.value}
                onChange={(e) => updateOption(index, { value: e.target.value })}
              />
              {languages.map((code) => (
                <input
                  key={code}
                  placeholder={code}
                  aria-label={code}
                  title={code}
                  value={opt.labels[code] || ''}
                  onChange={(e) => updateOption(index, { labels: { ...opt.labels, [code]: e.target.value } })}
                />
              ))}
              <button type="button" className="tp-icon tp-icon-danger" onClick={() => removeOption(index)} aria-label={t('common.delete')}>
                <NtIcon name="trash" size={15} />
              </button>
            </div>
          ))}
          <button type="button" className="nt-ghost-btn" onClick={addOption}>
            + {t('tickets.addOption')}
          </button>
        </div>
      )}

      {form.field_type === 'attachment' && (
        <div className="fd-edit-block">
          <div className="form-group fd-narrow">
            <label>{t('tickets.fieldMaxAttachments')}</label>
            <input
              type="number"
              min={1}
              max={20}
              value={form.maxAttachments}
              onChange={(e) => setForm({ ...form, maxAttachments: e.target.value })}
            />
            <p className="tp-hint">{t('tickets.fieldMaxAttachmentsHint')}</p>
          </div>
        </div>
      )}

      <div className="fd-edit-block fd-switches">
        <div className="au-active-row">
          <button
            type="button"
            role="switch"
            aria-checked={form.is_required}
            className={'au-switch' + (form.is_required ? ' au-switch-on' : '')}
            onClick={() => setForm({ ...form, is_required: !form.is_required })}
            aria-label={t('tickets.required')}
          >
            <span className="au-switch-knob" />
          </button>
          <span>{t('tickets.required')}</span>
        </div>
        {(!expandedId || expandedId === 'new') && (
          <div>
            <div className="au-active-row">
              <button
                type="button"
                role="switch"
                aria-checked={form.share_with_other_projects}
                className={'au-switch' + (form.share_with_other_projects ? ' au-switch-on' : '')}
                onClick={() => setForm({ ...form, share_with_other_projects: !form.share_with_other_projects })}
                aria-label={t('tickets.shareWithOtherProjects')}
              >
                <span className="au-switch-knob" />
              </button>
              <span>{t('tickets.shareWithOtherProjects')}</span>
            </div>
            <p className="tp-hint fd-switch-hint">{t('tickets.shareWithOtherProjectsHint')}</p>
          </div>
        )}
      </div>

      <div className="fd-edit-actions">
        <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn" disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );

  const renderLibraryItem = (key, field, meta, onAdd, busy) => (
    <div key={key} className="fd-lib-card">
      <span className="fd-type-icon" aria-hidden="true"><NtIcon name={FIELD_NT_ICON[field.field_type] || 'text'} size={16} /></span>
      <div className="fd-lib-text">
        <div className="fd-lib-name">{resolveFieldLabel(field, lang, defaultLanguage)}</div>
        <div className="fd-lib-meta">{meta}</div>
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onAdd} disabled={busy}>
        {busy ? t('common.adding') : `+ ${t('tickets.addExisting')}`}
      </button>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
      <PageHeading icon="layout" title={t('tickets.manageForm')} subtitle={project ? project.name : ''} />

      <div className="tp-toolbar">
        <button className="tpl-back fd-back" type="button" onClick={() => navigate('/ticket-projects')}>
          <NtIcon name="back" /> {t('tickets.backToQueues')}
        </button>
        <button className="btn" type="button" onClick={startCreate}>
          {t('tickets.addField')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <>
          <div className="fd-section-head">
            <h3>{t('tickets.usedFields')}</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddRecommended} disabled={seeding || missingRecommended.length === 0}>
              {seeding ? t('common.saving') : t('tickets.addRecommendedFields')}
            </button>
          </div>
          <p className="tp-hint fd-fixed-hint">
            <NtIcon name="key" size={13} /> {t('tickets.fixedFieldsHint')}
          </p>
          <div className="fd-list">
            {expandedId === 'new' && (
              <div className="fd-item fd-item-open">
                <div className="fd-row fd-row-new">
                  <span className="fd-type-icon" aria-hidden="true"><NtIcon name="plus" size={16} /></span>
                  <span className="fd-name">{t('tickets.addField')}</span>
                </div>
                {renderEditForm()}
              </div>
            )}
            {fields.length === 0 && expandedId !== 'new' ? (
              <p className="nt-empty">{t('tickets.noAdditionalFields')}</p>
            ) : (
              fields.map((field) => (
                <div key={field.id} className={'fd-item' + (expandedId === field.id ? ' fd-item-open' : '')}>
                  <div
                    className="fd-row"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      setDragId(field.id);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      handleDragOverRow(field.id);
                    }}
                    onDrop={(e) => e.preventDefault()}
                    onDragEnd={handleDragEnd}
                    onClick={() => (expandedId === field.id ? cancelEdit() : startEdit(field))}
                    style={{ opacity: dragId === field.id ? 0.35 : 1 }}
                  >
                    <span className="fd-grip" title={t('tickets.dragToReorder')} onClick={(e) => e.stopPropagation()}>
                      <NtIcon name="grip" size={16} />
                    </span>
                    <span className="fd-type-icon" aria-hidden="true"><NtIcon name={FIELD_NT_ICON[field.field_type] || 'text'} size={16} /></span>
                    <span className="fd-name">
                      {resolveFieldLabel(field, lang, defaultLanguage)}
                      <span className="fd-type-name">{t(`tickets.fieldType_${field.field_type}`)}</span>
                    </span>
                    {field.is_required && <span className="fd-required">{t('tickets.required')}</span>}
                    <button
                      type="button"
                      className="tp-icon tp-icon-danger fd-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(field);
                      }}
                      disabled={deletingId === field.id}
                      title={t('common.delete')}
                      aria-label={t('common.delete')}
                    >
                      <NtIcon name="trash" size={15} />
                    </button>
                    <span className="fd-chevron" aria-hidden="true">{expandedId === field.id ? '⌄' : '›'}</span>
                  </div>
                  {expandedId === field.id && renderEditForm()}
                </div>
              ))
            )}
          </div>

          <div className="fd-section-head fd-section-head-2">
            <h3>{t('tickets.availableFields')}</h3>
          </div>
          <p className="tp-hint">{t('tickets.availableFieldsHint')}</p>
          <div className="fd-lib">
            {recommendedWithUsage.map(({ field, usedInProjectNames }, index) => renderLibraryItem(
              `recommended-${index}`,
              field,
              <>
                {t(`tickets.fieldType_${field.field_type}`)}
                <span className="fd-tag">{t('tickets.recommendedFieldTag')}</span>
                {usedInProjectNames.length > 0 && ` · ${t('tickets.usedInProject', { name: usedInProjectNames.join(', ') })}`}
              </>,
              () => handleAddRecommendedSingle(field, index),
              addingRecommendedIndex === index,
            ))}
            {availableLibrary.map((field) => renderLibraryItem(
              field.id,
              field,
              `${t(`tickets.fieldType_${field.field_type}`)} · ${t('tickets.usedInProject', { name: field.usedInProjectNames.join(', ') })}`,
              () => handleAddFromLibrary(field),
              addingFieldId === field.id,
            ))}
            {availableLibrary.length === 0 && RECOMMENDED_FIELDS.length === 0 && (
              <p className="nt-empty">{t('tickets.noLibraryFields')}</p>
            )}
          </div>
        </>
      )}
      </div>
    </Layout>
  );
}

export default TicketProjectFields;
